import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'react-vant'
import CustomTextInput from '@/components/CustomTextInput'
import type { VerificationCodeDialogProps } from './index.type'
import CustomButton from '@/components/CustomButton'

export default React.memo<VerificationCodeDialogProps>(function VerificationCodeDialog({
    visible,
    onClose,
    account = '*********6651',
    onVerificationSuccess,
    onResendCode,
    title,
    description,
    submitLoading = false,
    resendLoading = false
}) {
    const { t } = useTranslation()
    const [code, setCode] = useState('')
    const [countdown, setCountdown] = useState(60)
    const [canResend, setCanResend] = useState(false)

    // 倒计时逻辑
    useEffect(() => {
        if (visible && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1)
            }, 1000)
            return () => clearTimeout(timer)
        } else if (countdown === 0) {
            setCanResend(true)
        }
    }, [countdown, visible])

    // 重置状态当弹窗显示时
    useEffect(() => {
        if (visible) {
            setCode('')
            setCountdown(60)
            setCanResend(false)
        }
    }, [visible])

    const handleCodeChange = useCallback((value: string) => {
        // 只允许输入数字，最多6位
        const numericValue = value.replace(/\D/g, '').slice(0, 6)
        setCode(numericValue)
    }, [])

    const handleResendCode = useCallback(() => {
        if (canResend && !resendLoading) {
            setCountdown(60)
            setCanResend(false)
            onResendCode?.()
        }
    }, [canResend, resendLoading, onResendCode])

    const handleSubmit = useCallback(() => {
        if (code.length === 6 && !submitLoading) {
            onVerificationSuccess?.(code)
        }
    }, [code, submitLoading, onVerificationSuccess])

    const formatCountdown = useCallback((seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }, [])

    return (
        <Dialog
            visible={visible}
            onClose={onClose}
            className="bg-[#F5F5F5]"
            closeable={true}
            showCancelButton={false}
            showConfirmButton={false}
            width="90%"
            style={{ zIndex: 10, maxHeight: '90vh'}}
        >
            {/* Content */}
            <div className="px-6 py-8 text-center bg-[#EEF0FD] max-h-[80vh] overflow-y-auto">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                    <img src="/images/register/send-code-icon.png" alt="验证码图标" />
                </div>

                {/* Title */}
                <div className="text-xl font-normal text-[#1A1A1A] mb-4">
                    {title || t('我们刚刚向您发送了验证码')}
                </div>

                {/* Description */}
                <p className="text-black mb-6 leading-relaxed text-xs">
                    {description || t('请输入我们发送至X的安全代码该代码将在10分钟后失效', { account })}
                </p>

                {/* Verification Code Input */}
                <div className="mb-6">
                    <CustomTextInput
                        placeholder={t('请输入您的6位数验证码')}
                        value={code}
                        onChangeText={handleCodeChange}
                        maxLength={6}
                        borderColor={'border-transparent'}
                        backgroundColor={'#ffffff'}
                        inputMode='numeric'
                    />
                </div>

                {/* Resend Code */}
                <div className="mb-6">
                    <div className="text-center">
                        <button
                            onClick={handleResendCode}
                            disabled={!canResend || resendLoading}
                            className={`text-3 font-medium transition-colors ${canResend && !resendLoading
                                    ? 'text-[var(--primary-color)]'
                                    : 'text-[#1A1A1A]'
                                }`}
                        >
                            {resendLoading
                                ? t('发送中')
                                : canResend
                                    ? t('重新发送验证码')
                                    : t('重新发送验证码X', { countdown: formatCountdown(countdown) })
                            }
                        </button>
                    </div>
                </div>

                {/* Submit Button */}
                <div className="space-y-3">
                    <CustomButton
                        onClick={handleSubmit}
                        loading={submitLoading}
                        disabled={code.length !== 6 || submitLoading}
                    >
                        {submitLoading ? t('验证中') : t('提交')}
                    </CustomButton>
                </div>
            </div>
        </Dialog>
    )
})